import {React} from '@api'
import {IconName} from '@blueprintjs/core'
import {Popover2InteractionKind} from '@api'
import { SnackbarOrigin } from '@material-ui/core'
import {PDFTemplateName} from '@modules/pdf'
import {LibraryItemConfig, WorkspaceConfig, DetailsViewsByIndex, ICatalogFeaturesConfig, WorkspaceChangeLogConfig, WorkspaceExportConfig, PdfExportConfig} from '@stores/config'
import {ICatalogItemChangeEventRaw, ICatalogUserSettings, StoresInstance, Workspace} from '@stores'

export interface ILoginConfig {
  /**
   * Something valid for a background: tag in CSS
   */
  background?: string

  sso?: SingleSignOnConfig

  elasticSearch?: {
    url: string,
    apiKey: string
  }
}

/**
 * Top level configuration for a given customer deployment
 *
 * Describes workspaces, libraries, settings, etc
 */
export interface DeploymentConfig {
  //_id?: string

  /**
   * Configurable things made available via an unautheticated route - YFTS-9635
   */
  loginConfig?: ILoginConfig

  title?: string
  version?: string

  contacts?: {
    centric?: { name?: string, email?: string }
    contacts?: Array<{ name?: string, email?: string }>
  }

/**
 * Base and sharable configuration for all the customers 
 *
 * e.g. usedIndexes
 */
  baseConfig?: IBaseConfig

  // TODO Used in refresh season dropdown - hardcoded for VLP
  syncDevHierarchy?: boolean
  syncParallelWorkspace?: boolean
  syncVersion?: string

  customer?: {
    name?: string,
    location?: string
  }
  updatedAt?: Date
  workspaces: Array<WorkspaceConfig>
  workspaceMenuHierarchy?: Array<IWorkspaceMenuHierarchy>

  pdf?: DeploymentPdfConfig

  changeLog?: WorkspaceChangeLogConfig

  libraries?: Array<LibraryItemConfig>
  detailViews: DetailsViewsByIndex

  settings?: Partial<GlobalDeploymentSettings>

  languages?: LanguagesConfig[]

  hideC8Button?: boolean

  availableIFrames?: Array<ExternalIFrameConfig>

  /**
   * Arbitrary urls/slug with specialized behaviors
   *
   * Something like /product-board/Some-Human-Readable-Slug
   * NYI
   */
  routing?: Record<string, DeploymentRoutingConfig>

  views?: IViewOptions
}

export interface DeploymentPdfConfig {
  /**
   * See sam-create-pdf-in-s3 project
   */
  renderApi?: {
    baseUrl?: string,
    env?: string,
    host?: string,
    apiKey?: string
  }
  templates?: Array<PDFTemplateName>
  pdfDownloadName?: string,
  fieldCount?: number
  fonts?: Array<{ name?, path? }>
  versions?: string[]
  types?: string[]
  defaultTemplate?: PdfExportConfig
}

export interface ImagesConfig {
  alwaysUseC8?: boolean
}

/**
 * SSO - https://centric8.atlassian.net/browse/YFTS-5496
 */
export type SingleSignOnConfig = C8SingleSignOnConfig

export interface C8SingleSignOnConfig{
  source: 'c8'
  c8Url: string
}

export interface DeploymentRoutingConfig {

}

export interface IViewOptions {
  tableView?: boolean,
  pdfView?: boolean,
  cardView?: boolean
}

export interface ExternalIFrameConfig {
  label: string
  category?: string
  url: string
  icon?: IconName
  injectCss?: string
}

export interface IUsedIndex {
  name: string, 
  shouldMap: boolean //Is index liable to custom mapping
  _id?: string
}  

export interface IBaseConfig {
  usedIndexes: Array<IUsedIndex>
}

export interface LanguagesConfig {
  language: string
  label?: string
  default?: boolean
}

export interface FlexLayoutSettings {
  autoUndo?: boolean // expensive - turn off

  /**
   * Creating a details item opens a split which means the thing that opened it may be moved offscreen, after which we want to scrollIntoView() it
   */
  scrollIntoViewDelayAfterNewChildTab?: number
  throttleResizeMs?: number
  tabGroups?: boolean
  layoutPreviewMs?: number
}

export interface WhiteboardDeploymentSettings {

}

/**
 * {@see DEFAULT_DEPLOYMENT_SETTINGS}
 */
export interface GlobalDeploymentSettings {
  es?: { maxBatchQueue?: number}
  staggerLevelOfDetail?: boolean
  socketIO?: boolean
  pdf?: {
   target?: 'client' | 'server'
  }

  allowReimportImage?: boolean

  /**
   * Dropdown to navigate from MPV -> Style -> Colorways (or wherever) in a detail pane
   */
  detailPaneTabDropdownNav?: boolean
  /** Defaults to cloudfront - The rest are more for debugging purposes than anything  */
  imageSource?: 'c8' | 'cloudfront'
  changeHistory?: {
    enabled?: boolean
  }
  hideHeaderAfterScrollDelay?: boolean
  services?: {nest?: boolean}
  maxImagesPerUpload?: number
  viewOptions?: {allowAllButton?: boolean}
  doubleClickMs?: number
  sync?: { pollInterval?: number }
  groupByHeroCards?: boolean
  c8?: {
    /**
     * Add session URLs to C8 urls?
     */
    sso?: boolean
  },
  assortments?: {
    /**
     * Generally called an 'Assortment', RH calls it a 'Product Board'
     */
    assortmentNoun?: string
    itemsPerQuery?: number
    maxSwatches?: number
  }
  reports?: {
    clearJustLoadedReportTimeout?: number
    draftReports?: boolean
    hideToolbarsAfterScrollDelay?: number
    /**
     * This is used twice - once to determine how often to diff things (CPU intensive) and again to determine when to send changes up to ES (network intensive)
     */
    updateSettingsDebounce?: number
    defaultName?: string
  }
  updateCardViewDebounce?: number
  showGroupButtonsInTopBar?: boolean
  syncSnackbarLocation?: SnackbarOrigin
  dragImagesToDesktop?: boolean
  flexLayout?: FlexLayoutSettings
  batch?: { ms?: number, max?: number}

  /**
   * An image background to inject into CSS for things like builder cards, list preview cards, etc
   * {@see BuilderCard} {@see ListPreviewCard}
   */
  imageBackground?: string | {exId: string}

  otherImageIds?: Array<string>

  images?: ImagesConfig

  table?: {
    licenseKey: string
  }

  whiteboard?: WhiteboardDeploymentSettings
  defaultEditable?: boolean

  /**
   * Automatically sanitize the config to add missing libraries, sections, etc
   */
  sanitize?: boolean

  /**
   * Custom font families to register - files should exist in /public/fonts/<your path>
   */
  fonts?: { [family: string]: string }

  /**
   * Should we try to use an intersection observer and avoid loading groups that are offscreen?
   */
  renderOffscreenGroups?: boolean

  experimental?: {
    imageAnnotationInModal?: boolean
  }
  filter?: {
    maxValuesToLoadPerGroup?: number

    /**
     * If we instantly save off input keystrokes to a variable that will cause our filter groups to rerender (usually due to highlighting text or hiding the group) the GUI will stutter as the user types.
     * Instead save it to a local observable in the {@see SearchFilterStore}
     */
    debounceLocalSearchMs?: number

    /**
     * Don't query ES for new filter data until user has stopped typing for at least this long
     */
    debounceSearchEsMs?: number

    /**
     * How long after the state of the catalog has changed before we should search
     * Changes to state.search will cause this to fire as will navigating down the hierarchy or filtering elsewhere
     */
    debounceRefetchFiltersEs?: number
  }
  maxHitsForSpecGroupAssociatedItems?: any
  /** Allow QR Code creation?  **/
  qr?: boolean
  hierarchyDebounceMs?: number
  configMenu?: boolean
  sidebarWidth?: {left?: number, right?: number }
  notificationPollInterval: number
  publishedItemPollInterval: number
  titleBarHeight?: number,

  security?: {
    isWorkspaceVisible?: ((workspace: Workspace, stores: StoresInstance) => boolean)
  }

  defaults: {
    catalog?: {
      userSettings?: Partial<ICatalogUserSettings>
      cardsPerVirtualRow?: number
      fieldBehavior?: { group?: boolean, filter?: boolean, sort?: boolean, editable?: boolean, all?: boolean },
      /**
       * YFTS-9764 Allow opt-out of comments in modals
       */
      comments?: boolean
      export?: WorkspaceExportConfig
      features?: ICatalogFeaturesConfig
    }
  },

  PRELOAD_HOVER_TIME: number,
  AUTOHIDE_SNACKBAR_DURATION: number,
  HOVER_TIMEOUT: number,
  cardPopover?: Partial<CardPopoverSettings>

  maxGroupDepth: number,
  maxSubtitlesPerGroup: number,

  noPublish?: boolean,
  // We wait to show cards until every sibling in a group has finished loading - but this can take awhile or things can be offscreen so if things are still waiting, stop and show the cards
  defaultProductName: 'Centric Visual Innovation Platform',
  maxNestedItemsToShow: number,
  updateIndicatorMs: number,
  showUserSettings?: boolean,
  /**
   * When navigating to the next/prior catalog item we want to take enough time to actually animate the unload/load
   */
  modalSwitchItemDelayMs: number,
  pivotView: boolean,
  showNoValueAsEmpty?: boolean,
  showWorkspaceTitleWithLibraries?: boolean
  // deleteInfo?: IDeleteInfo,
  showHeaderSyncIndicator?: boolean
  showC8ButtonInTopBar?: boolean
  maxChildGroupColumns?: number
  allowGridChanges?: boolean
  usingBulkAssignAPI?:boolean
  maxCatalogItemOrderByTerms?: number
  dragDropImageWidth?: number
  numberOfPDFVisibleViewOptions?: number
  showSortedVersions?: boolean
  sortDefaultAttributes?: boolean
  inlineCardFieldEditing?: boolean
  attributesInCreate?: boolean
  showUnsavedPerspectiveMenu?: boolean
  noImagePlaceholderStyle?: Partial<React.CSSProperties>
  /**
   * YFTS-7851
   */
  hideFilteredSearchResults?: boolean
}

export interface CardPopoverSettings {
  ms?: number,
  closeMs?: number,
  hideHoverButtonsMs?: number
  interactionKind?: Popover2InteractionKind
}

// export interface IDeleteInfo {
//   markAsDelete?: boolean,
//   deleteFieldName?: string,
// }

export interface IWorkspaceMenuHierarchy {
  label: string
  icon?: IconName | JSX.Element
  workspaceIds: Array<string>
}
export interface ISyncIndexMapping {
  index: string,
  customViewName: string,
  nested?: any,
  keyMap?: object,
  imageSources?: string[],
  fieldTypes?: object
}
export interface ISyncGlobalConfig {
  diffForChanges?: IDiffForChanges,
  incrementalCronInterval: string,
  fullViewCronInterval?: string
}
interface IDiffForChanges {
  sendImagesToCloudFront?: Boolean,
  updateOnlyDifferences?: Boolean,
}
export interface ICronConfig{
  name: string,
  interval: string,
  scheduled?: boolean,
  timezone?: string,
  enable: boolean
}

export const DEFAULT_CHANGE_LOG_CONFIG = {
  additional: [
    {
      type: 'datetime',
      label: 'Date',
      values: ['start', 'end']
    }, {
      type: 'operation',
      label: 'Action',
      values: ['Added', 'Changed', 'Deleted']
    },
    {
      label: 'User',
      type: 'user'
    },
    {
      label: 'Workspace',
      type: 'workspace'

    }
  ],
  fields: ['_log.delta.text', '_log.changes.diff.after.text', '_log.changes.diff.before.text', '_log.changes.field.text', '_log.changes.op.text', 'name.text'],
  exportFields: [
    {
      field: '_log.timestamp',
      label: 'Date Created',
      type: 'DateTime'
    },
    {
      field: '_log.changes',
      label: 'Action',
      subfield: 'op',
      type: 'String'
    },
    {
      field: '_log.index',
      label: 'Object',
      type: 'String'
    },
    {
      field: 'style.attributes.thlGarmentSubcategory',
      label: 'Fabrication',
      type: 'String'
    },
    {
      field: 'collection.name',
      label: 'Collection',
      type: 'String'
    },
    {
      field: 'style.theme.name',
      label: 'Theme',
      type: 'String'
    },
    {
      field: 'style.attributes.thlDesignNo',
      label: 'Body',
      type: 'String'
    },
    {
      field: 'style.name',
      label: 'Style',
      type: 'String'
    },
    {
      field: 'style.attributes.thlDesignNo',
      label: 'STYLE #',
      type: 'String'
    },
    {
      field: 'name',
      label: 'Colorway',
      type: 'String'
    },
    {
      field: '_log.userId',
      label: 'User',
      type: 'String'
    },
    {
      field: 'season.name',
      label: 'Season',
      type: 'String'
    },
    {
      field: 'attributes.thlSMSCountVip',
      label: 'SMS REQUEST',
      type: 'Int'
    },
    {
      field: 'attributes.notes',
      label: 'NOTES',
      type: 'String'
    }
  ]
}

export const DEFAULT_DEPLOYMENT_CONFIG: Partial<DeploymentConfig> = {
  pdf: {
    renderApi: {
      baseUrl: 'https://jv1w1cis58.execute-api.us-west-2.amazonaws.com',
      apiKey: '8V9fLM2BVy4BYrqiu2QnvaWMxB7EIOak6YdRLN8v',
      host: 'vip-dev',
      env: 'dev'
    },
  }
}



// WEBPACK FOOTER //
// ./src/stores/config/deployment/DeploymentConfig.ts